import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useT } from '../../i18n';
import { useSession } from '../../store/session';
import { useUi } from '../../store/ui';
import { ApiError, NetworkError } from '../../sync/api';
import controls from '../controls.module.css';
import styles from './Auth.module.css';
import { AuthLayout, Field } from './AuthLayout';

export function ChangePassword() {
  const t = useT();
  const navigate = useNavigate();
  const user = useSession((s) => s.user);
  const changePassword = useSession((s) => s.changePassword);
  const showToast = useUi((s) => s.showToast);
  const forced = user?.mustChangePassword === true;
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (next !== confirm) {
      setError(t.t('password.mismatch'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changePassword(next, forced ? undefined : current);
      showToast(t.t('password.saved'));
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.code === 'VALIDATION' ? t.error('VALIDATION') : err.code === 'BAD_CREDENTIALS' ? t.t('auth.error.BAD_CREDENTIALS') : t.error(err.code));
      else if (err instanceof NetworkError) setError(t.t('auth.serverOffline'));
      else setError(t.error(undefined));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={forced ? t.t('password.forced') : t.t('password.title')}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        {forced ? null : (
          <Field label={t.t('password.current')}>
            <input className={controls.field} type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
          </Field>
        )}
        <Field label={t.t('password.next')}>
          <input className={controls.field} type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required minLength={10} />
        </Field>
        <Field label={t.t('password.confirm')}>
          <input className={controls.field} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
        </Field>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <button type="submit" className={`${controls.btn} ${controls.primary}`} disabled={busy}>
          {busy ? t.t('auth.working') : t.t('password.save')}
        </button>
      </form>
      {forced ? null : (
        <p className={styles.footer}>
          <Link to="/settings" className={styles.link}>
            {t.t('common.cancel')}
          </Link>
        </p>
      )}
    </AuthLayout>
  );
}
